import { detectWithRegex } from '../detect/pii/regex';
import { detectFormFields } from '../detect/pii/forms';
import { detectNames } from '../detect/pii/names';
import { mergeDetections } from '../detect/pii/merge';
import { detectLanguage, type DetectedLanguage } from '../detect/pii/language';
import type { TextDetection } from '../detect/pii/types';
import { ok, err, type Result } from '../lib/types';
import {
  dispatchImage,
  type DispatchOpts,
  type ImageDispatchValue,
} from './image';
import { dispatchPdf, type PdfDispatchValue } from './pdf';

export type { DispatchOpts, DispatchProgress, DispatchProgressKind } from './image';

export type FileKind = 'text' | 'csv' | 'pdf' | 'image' | 'unsupported';

type TextLikeFields = {
  filename: string;
  outputFilename: string;
  originalText: string;
  detections: TextDetection[];
  language?: DetectedLanguage;
};

export type TextDispatchValue = ({ kind: 'text' } | { kind: 'csv' }) & TextLikeFields;

export type DispatchValue = TextDispatchValue | ImageDispatchValue | PdfDispatchValue;

const TEXT_EXTENSIONS = new Set(['txt', 'md', 'log']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'heic', 'heif', 'webp']);

const extensionOf = (name: string): string => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
};

export const detectKind = (file: File): FileKind => {
  const ext = extensionOf(file.name);
  if (ext === 'csv' || file.type === 'text/csv') return 'csv';
  if (TEXT_EXTENSIONS.has(ext)) return 'text';
  if (file.type === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (file.type.startsWith('image/') || IMAGE_EXTENSIONS.has(ext)) return 'image';
  if (file.type.startsWith('text/')) return 'text';
  return 'unsupported';
};

const outputName = (kind: 'text' | 'csv'): string =>
  kind === 'csv' ? 'redacted.csv' : 'redacted.txt';

const dispatchText = async (file: File, kind: 'text' | 'csv'): Promise<Result<DispatchValue>> => {
  let originalText: string;
  try {
    originalText = await file.text();
  } catch (cause) {
    return err({ code: 'READ_FAILED', message: 'Could not read this file.', cause });
  }
  const [namesDets, language] = await Promise.all([
    detectNames(originalText),
    detectLanguage(originalText),
  ]);
  const raw = [
    ...detectWithRegex(originalText),
    ...detectFormFields(originalText),
    ...namesDets,
  ];
  const detections = mergeDetections(raw);
  return ok({
    kind,
    filename: file.name,
    outputFilename: outputName(kind),
    originalText,
    detections,
    ...(language !== undefined && { language }),
  });
};

/**
 * Dispatch a single file to its detection pipeline.
 */
export const dispatch = async (
  file: File,
  opts: DispatchOpts = {},
): Promise<Result<DispatchValue>> => {
  const kind = detectKind(file);
  if (kind === 'text' || kind === 'csv') return dispatchText(file, kind);
  if (kind === 'image') return dispatchImage(file, opts);
  if (kind === 'pdf') return dispatchPdf(file, opts);
  return err({
    code: 'UNSUPPORTED_KIND',
    message: "We don't recognize this file type. Try a PDF, image, text, or CSV file.",
  });
};
